/**
 * The spending limit's one reading (task 1159).
 *
 * Report: `project/reports/innovate-account-spending-limit-and-alerts.md`.
 * Mockup: `project/artifacts/innovate-app-settings-billing-spending-mockup.html`
 * (frame 6 is the colour and state table this module encodes).
 *
 * Every spending surface — the Spending page, the Billing usage row, the
 * Dashboard tile, the top-bar indicator and the two banners — derives its state
 * from the usage summary's `spending` block through `spendingReading`, so they
 * never disagree:
 *   - indigo: a limit is set and no threshold alert has been sent this month
 *   - amber:  a threshold alert was sent and calls continue (including past
 *             100% with the hard stop off — the bar fills, the percentage keeps
 *             counting)
 *   - red:    `stopped`: the hard stop is refusing external calls
 */
import type { AccountSpending } from '../types/billing';

/** Where Settings › Billing › Spending lives. */
export const SPENDING_PAGE_PATH = '/settings/billing/spending';

/** The thresholds a new limit starts with (report, open decision 4). */
export const DEFAULT_ALERT_THRESHOLDS_PCT = [50, 80, 100];

/** At most this many thresholds per account (service task 1156 validation). */
export const MAX_ALERT_THRESHOLDS = 5;

export type SpendingTone = 'indigo' | 'amber' | 'red';

export interface SpendingReading {
  /** A limit is set. Without one there is no bar, no colour and no exception. */
  hasLimit: boolean;
  limitCents: number | null;
  /** Billed cents this month; null when the service could not resolve the plan's markup. */
  billedCents: number | null;
  /** Billed usage as a whole percentage of the limit; may exceed 100. Null without a limit. */
  pct: number | null;
  /** At least one threshold alert was sent this month. */
  crossed: boolean;
  /** The hard stop is refusing external calls. */
  stopped: boolean;
  /** The highest threshold alerted this month, or null. */
  highestAlertPct: number | null;
  tone: SpendingTone;
}

/** Read the `spending` block. Absent (a service build predating task 1156) reads as no limit. */
export function spendingReading(spending: AccountSpending | null | undefined): SpendingReading {
  const limitCents = spending?.limitCents && spending.limitCents > 0 ? spending.limitCents : null;
  // `billedCents` is null only when the service could not resolve the plan;
  // an absent block (no service support) reads as zero spend with no limit.
  const rawBilled = spending ? spending.billedCents : 0;
  const billedCents = rawBilled === null ? null : Math.max(0, rawBilled);
  const alerts = spending?.alertsSent ?? [];
  const hasLimit = limitCents !== null;
  const stopped = hasLimit && spending?.stopped === true;
  const crossed = hasLimit && alerts.length > 0;
  const highestAlertPct = alerts.length > 0 ? Math.max(...alerts.map((a) => a.thresholdPct)) : null;
  const pct = hasLimit && billedCents !== null ? Math.floor((billedCents / (limitCents as number)) * 100) : null;
  return {
    hasLimit,
    limitCents,
    billedCents,
    pct,
    crossed,
    stopped,
    highestAlertPct,
    tone: stopped ? 'red' : crossed ? 'amber' : 'indigo',
  };
}

/** Bar fill classes per tone. */
export const SPENDING_BAR_CLASSES: Record<SpendingTone, string> = {
  indigo: 'bg-indigo-500 dark:bg-indigo-400',
  amber: 'bg-amber-500 dark:bg-amber-400',
  red: 'bg-red-500 dark:bg-red-400',
};

/** Text classes per tone, for the percentage and the indicator. */
export const SPENDING_TEXT_CLASSES: Record<SpendingTone, string> = {
  indigo: 'text-indigo-600 dark:text-indigo-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Cents as dollars with cents: 16000 → "$160.00". */
export function formatSpendingCents(cents: number): string {
  return usd.format(cents / 100);
}

/** Billed cents for display; an em dash when the service could not compute them. */
export function formatBilledCents(cents: number | null, short = false): string {
  if (cents === null) return '—';
  return short ? formatSpendingCentsShort(cents) : formatSpendingCents(cents);
}

/** Cents as whole dollars when there are no cents: 20000 → "$200", 20075 → "$200.75". */
export function formatSpendingCentsShort(cents: number): string {
  return cents % 100 === 0 ? `$${(cents / 100).toLocaleString('en-US')}` : formatSpendingCents(cents);
}

/** The month a usage period key names: "2026-10" → "October 2026". */
export function formatSpendingMonth(period: string | null | undefined, withYear = true): string | null {
  const match = period ? /^(\d{4})-(\d{2})$/.exec(period) : null;
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString('en-US', {
    month: 'long',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

/** "Oct 12, 14:03" for an alert's sent time. */
export function formatAlertTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "Oct 20" for the day the limit was reached. */
export function formatAlertDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * The refusal explanation for `spending_limit_reached`, by who is reading it.
 * Owners and admins can act on the Spending page; members are pointed at the
 * account owner. Same split as the existing plan refusals.
 */
export function spendingRefusalMessage(canManage: boolean): string {
  return canManage
    ? 'Your account has reached its monthly spending limit. Raise the limit or turn off the hard stop at Settings → Billing → Spending.'
    : 'Your account has reached its monthly spending limit. Contact your account owner.';
}

/**
 * The line a schedule's run log shows beside a refused run. A run refused by
 * the spending limit carries the code `spending_limit_reached`; it reads as the
 * role-aware explanation instead of the raw code. Every other detail is shown
 * as the service wrote it, as before.
 */
export function scheduleRunDetailText(detail: string | null, canManage: boolean): string | null {
  if (detail === 'spending_limit_reached') return spendingRefusalMessage(canManage);
  if (detail === SERVICE_STOPPED_CODE) return SERVICE_STOPPED_TEXT;
  return detail;
}

/**
 * The AI Service's stop gate (task 1163) refuses with `service_stopped`, and
 * the schedule worker pauses on the first such refusal. The pause detail may
 * carry the code (as plan refusals do today) or task 1163's sentence; both
 * read as one line (task 1164).
 */
const SERVICE_STOPPED_CODE = 'service_stopped';
const SERVICE_STOPPED_TEXT = 'Service is stopped';
const SERVICE_STOPPED_DETAILS = new Set([SERVICE_STOPPED_CODE, 'AI Service is stopped']);

/** The pause reason a Schedule row shows. Every other detail is shown as written. */
export function scheduleStatusDetailText(detail: string | null): string | null {
  if (detail && SERVICE_STOPPED_DETAILS.has(detail.trim())) return SERVICE_STOPPED_TEXT;
  return detail;
}
